import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import type { FormSnippet } from '@ipfabric/ui-components/utils/formSnippets/FormSnippet';
import { hookFormZod } from '@ipfabric/ui-components/utils/hookForm/hookFormZod';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  FULL_BGP_LIMIT_MAX,
  FULL_BGP_LIMIT_MIN,
} from '../../../../../../../packages/api-schemas/src/lib/settings/discovery/fullBgpLimit';
import type { DiscoverySettingsObject } from '../../model/DiscoverySettingsObject';

type FullBgpIpv6LimitFieldValues = {
  fullBgpIpv6LimitEnabled: boolean;
  fullBgpIpv6Limit: string;
};
type FullBgpIpv6LimitOutputValues = {
  fullBgpIpv6LimitEnabled: boolean;
  fullBgpIpv6Limit: number;
};

const FullBgpIpv6LimitField = (props: {
  form: UseFormReturn<
    FullBgpIpv6LimitFieldValues,
    unknown,
    FullBgpIpv6LimitOutputValues
  >;
}) => {
  const fullBgpIpv6LimitEnabled = useWatch({
    control: props.form.control,
    name: 'fullBgpIpv6LimitEnabled',
  });
  return (
    <div className='flex flex-col gap-3xs'>
      <FormSwitch
        control={props.form.control}
        name='fullBgpIpv6LimitEnabled'
        label='Use threshold limit for IPv6'
      />
      <div className='w-60 ml-12'>
        <FormTextField
          control={props.form.control}
          type='number'
          name='fullBgpIpv6Limit'
          disabled={!fullBgpIpv6LimitEnabled}
        />
      </div>
    </div>
  );
};

export const fullBgpIpv6Limit: FormSnippet<
  DiscoverySettingsObject,
  FullBgpIpv6LimitFieldValues,
  FullBgpIpv6LimitOutputValues
> = {
  render: ({ form }) => <FullBgpIpv6LimitField form={form} />,
  fromModel: (model, fieldValuesDraft) => {
    fieldValuesDraft.fullBgpIpv6LimitEnabled =
      model.fullBgpIpv6Limit?.enabled ?? false;
    fieldValuesDraft.fullBgpIpv6Limit =
      model.fullBgpIpv6Limit?.threshold?.toString() ?? '';
  },
  toModel: (transformedValues, modelDraft) => {
    modelDraft.fullBgpIpv6Limit = {
      enabled: transformedValues.fullBgpIpv6LimitEnabled,
      threshold: transformedValues.fullBgpIpv6Limit,
    };
  },
  schema: z.object({
    fullBgpIpv6LimitEnabled: z.boolean(),
    fullBgpIpv6Limit: hookFormZod.numberInputWith(
      hookFormZod.minMax(FULL_BGP_LIMIT_MIN, FULL_BGP_LIMIT_MAX),
    ),
  }),
};
